import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { Save, Link as LinkIcon, ScanLine } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';
import BarcodeScannerDialog from '../../../components/BarcodeScannerDialog';
import { findDeviceByScan, getDeviceSelectValue } from '../../../utils/deviceScanMatch';

export default function DeviceAssignments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoaded, setAssignmentsLoaded] = useState(false);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const { post, get } = useApi();

  /**
   * Helpers to read ids safely from user + objects
   */
  const getUserId = useCallback(() => (
    user?.id || user?._id || user?.userId || user?.user_id || null
  ), [user]);

  const getAccountIdFromUser = useCallback(() => (
    user?.account_id || user?.accountId || user?.account?.id || user?.account?._id || null
  ), [user]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      device_id: '',
      account_id: '', // will be set to the actual ACCOUNT uuid (not the user uuid)
      notes: '',
      assigned_by: '' // will be set to current admin user uuid
    }
  });

  const watchedAccountId = watch('account_id');

  /**
   * Load order: assignments → accounts → devices
   */
  useEffect(() => {
    (async () => {
      await fetchAssignments();
      await fetchAccounts();
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (assignmentsLoaded) fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, assignmentsLoaded]);

  /**
   * Ensure assigned_by is always the current admin's UUID
   */
  useEffect(() => {
    const uid = getUserId();
    if (uid) setValue('assigned_by', uid, { shouldDirty: true });
  }, [getUserId, setValue]);

  /**
   * Fetch existing assignments (to filter already assigned devices)
   */
  const fetchAssignments = async () => {
    try {
      const resp = await get('/device-assignments?per_page=1000');
      let data = [];
      if (Array.isArray(resp?.data)) data = resp.data; // { data: [...] }
      else if (resp?.success && Array.isArray(resp?.data)) data = resp.data;
      else if (Array.isArray(resp)) data = resp;

      const active = (data || []).filter(a => {
        const isUnassigned = a.unassignedAt || a.unassigned_at;
        const deviceStatus = a.device?.deviceStatus || a.device?.device_status;
        return !isUnassigned && deviceStatus !== 'Unassigned';
      });
      setAssignments(active);
      setAssignmentsLoaded(true);
    } catch (e) {
      setAssignments([]);
      setAssignmentsLoaded(true);
    }
  };

  /**
   * Fetch devices and filter out those already assigned
   */
  const fetchDevices = async () => {
    setIsLoadingDevices(true);
    try {
      const resp = await get('/devices');
      let data = [];
      if (resp?.success && Array.isArray(resp?.data)) data = resp.data;
      else if (Array.isArray(resp?.data)) data = resp.data;
      else if (Array.isArray(resp)) data = resp;

      const assignedIds = new Set();
      assignments.forEach(asg => {
        const id = asg.device_id || asg.device?._id || asg.device?.id || asg.device?.device_id;
        if (id) assignedIds.add(id);
      });

      const unassigned = (data || []).filter(d => {
        const id = d._id || d.id || d.device_id;
        const status = d.deviceStatus || d.device_status;
        return !assignedIds.has(id) && status !== 'Assigned';
      });

      setDevices(unassigned);
    } catch (e) {
      toast.error('Failed to fetch devices');
      setDevices([]);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  /**
   * Fetch accounts and pre-select the current admin's ACCOUNT (not user id)
   */
  const fetchAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      const resp = await get('/accounts');
      let data = [];
      if (resp?.success && Array.isArray(resp?.data)) data = resp.data;
      else if (Array.isArray(resp?.data)) data = resp.data;
      else if (Array.isArray(resp)) data = resp;

      const uid = getUserId();
      const userAccountId = getAccountIdFromUser();

      // Try several safe matches to find the admin's own account row
      const mine = (data || []).filter(acc => (
        acc.id === userAccountId ||
        acc._id === userAccountId ||
        acc.owner_id === uid ||
        acc.user_id === uid ||
        acc.userId === uid ||
        acc.email === user?.email
      ));

      setAccounts(mine.length ? mine : data);

      // Prefer explicit account_id from the user object; otherwise first match
      const preselectId = userAccountId || mine?.[0]?._id || mine?.[0]?.id || null;
      if (preselectId) {
        setValue('account_id', preselectId, { shouldDirty: true });
      }
    } catch (e) {
      toast.error('Failed to fetch accounts');
      setAccounts([]);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  /**
   * Submit: account_id must be an ACCOUNT uuid; assigned_by must be the admin's USER uuid
   */
  const onSubmit = async (form) => {
    setIsLoading(true);
    try {
      const uid = getUserId();
      const payload = {
        device_id: form.device_id,
        account_id: form.account_id,          // <- account uuid
        notes: form.notes || '',
        assigned_by: form.assigned_by || uid, // <- admin (user) uuid
      };

      if (!payload.device_id) {
        toast.error('Please select a device');
        setIsLoading(false);
        return;
      }
      if (!payload.account_id) {
        toast.error('Please select an account');
        setIsLoading(false);
        return;
      }
      if (!payload.assigned_by) {
        toast.error('Missing assigned_by (admin id)');
        setIsLoading(false);
        return;
      }

      // Optional safety: warn if the two ids accidentally match
      if (payload.account_id === payload.assigned_by) {
      }

      const resp = await post('/device-assignments', payload);

      // Basic success gate
      if (resp?.success === false) {
        throw new Error(resp?.message || 'Assignment failed');
      }

      toast.success('Device assigned successfully!');
      navigate('/admin/device-assignments/all');
    } catch (error) {
      const apiMsg = error?.response?.data?.message;
      const message = apiMsg || error.message || 'An error occurred while assigning the device.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl bg-background text-foreground min-h-screen">
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold">Assign Device</h1>
          <p className="text-muted-foreground">Assign a device to an account</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card className="border-0 shadow-none ">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <LinkIcon className="w-5 h-5" />
              Device Assignment
            </CardTitle>
            <CardDescription>
              Select device and account for assignment
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Device Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="device_id">Device *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsScannerOpen(true)}
                  disabled={isLoadingDevices || devices.length === 0}
                >
                  <ScanLine className="mr-1.5 h-3.5 w-3.5" />
                  Scan
                </Button>
              </div>
              <Select onValueChange={(v) => setValue('device_id', v)} disabled={isLoadingDevices || devices.length === 0}>
                <SelectTrigger className="w-full border-0">
                  <SelectValue placeholder={
                    isLoadingDevices
                      ? 'Loading devices...'
                      : devices.length === 0
                        ? 'No unassigned devices available'
                        : 'Select a device'
                  } />
                </SelectTrigger>
                <SelectContent className='border-0 '>
                  {devices.length === 0 && !isLoadingDevices ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">No unassigned devices available</div>
                  ) : (
                    devices.map((d) => {
                      const name = d.deviceName || d.device_name || 'N/A';
                      const status = d.deviceStatus || d.device_status || 'N/A';
                      const imei = d.imeiNumber || d.imei_number || d.imei || 'N/A';
                      const value = d._id || d.id || d.device_id;
                      return (
                        <SelectItem key={value} value={value}>
                          {name} - {status} - {imei}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
              {errors.device_id && (
                <p className="text-sm text-red-500">{errors.device_id.message}</p>
              )}
            </div>

            {/* Account Selection (auto-preselected to current admin's account, but not forced to user id) */}
            <div className="space-y-2">
              <Label htmlFor="account_id">Account *</Label>
              <Select
                onValueChange={(v) => setValue('account_id', v)}
                disabled={isLoadingAccounts || accounts.length === 0}
                value={watchedAccountId || undefined}
              >
                <SelectTrigger className="w-full border-0">
                  <SelectValue placeholder={
                    isLoadingAccounts
                      ? 'Loading accounts...'
                      : accounts.length > 0
                        ? 'Select an account'
                        : 'No accounts available'
                  } />
                </SelectTrigger>
                <SelectContent className='border-0'>
                  {accounts.map((acc) => {
                    const value = acc._id || acc.id;
                    return (
                      <SelectItem key={value} value={value}>
                        {acc.name || 'Account'} ({acc.email || 'no-email'})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">This will be saved as the <strong>account_id</strong> in the assignment.</p>
              {errors.account_id && (
                <p className="text-sm text-red-500">{errors.account_id.message}</p>
              )}
            </div>

            {/* Hidden assigned_by tied to RHF state (set via useEffect) */}
            <input type="hidden" {...register('assigned_by')} />

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" placeholder="Enter assignment notes..." className="min-h-[100px]" {...register('notes')} />
              {errors.notes && (
                <p className="text-sm text-red-500">{errors.notes.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={isLoading} className='border-muted-foreground/10 border-1'>
            Cancel
          </Button>
          <Button type="submit" variant="outline" disabled={isLoading || isLoadingDevices || isLoadingAccounts} className="flex items-center gap-2 border-muted-foreground/10 border-1">
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 mr-2" />
                Assigning...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Assign Device
              </>
            )}
          </Button>
        </div>
      </form>

      <BarcodeScannerDialog
        open={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        title="Scan device to select"
        onScan={(value) => {
          const match = findDeviceByScan(devices, value);
          const deviceId = getDeviceSelectValue(match);
          if (!deviceId) {
            toast.error('No matching unassigned device found for scanned code.');
            return;
          }
          setValue('device_id', deviceId, { shouldValidate: true, shouldDirty: true });
          toast.success('Device selected from scan.');
        }}
      />
    </div>
  );
}
